"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTransfer } from "./TransferState";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import { CommunityMemberMinimal } from "@/entities/IProfile";
import { ContractType } from "@/enums";
import { ApiIdentity } from "@/generated/models/ApiIdentity";
import { getUserProfile } from "@/helpers/server.helpers";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useIdentity } from "@/hooks/useIdentity";
import { commonApiFetch } from "@/services/api/common-api";
import { faAnglesDown, faAnglesUp, faExclamationTriangle, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { createPortal } from "react-dom";
import { Address, isAddress, PublicClient, type WriteContractParameters } from "viem";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import TransferModalPfp from "./TransferModalPfp";
const MIN_SEARCH_LENGTH = 3;
const ERC721_ABI = [{
  type: "function",
  name: "safeTransferFrom",
  stateMutability: "nonpayable",
  inputs: [{
    name: "from",
    type: "address"
  }, {
    name: "to",
    type: "address"
  }, {
    name: "tokenId",
    type: "uint256"
  }],
  outputs: []
}] as const;
const ERC1155_ABI = [{
  type: "function",
  name: "safeTransferFrom",
  stateMutability: "nonpayable",
  inputs: [{
    name: "from",
    type: "address"
  }, {
    name: "to",
    type: "address"
  }, {
    name: "id",
    type: "uint256"
  }, {
    name: "amount",
    type: "uint256"
  }, {
    name: "data",
    type: "bytes"
  }],
  outputs: []
}, {
  type: "function",
  name: "safeBatchTransferFrom",
  stateMutability: "nonpayable",
  inputs: [{
    name: "from",
    type: "address"
  }, {
    name: "to",
    type: "address"
  }, {
    name: "ids",
    type: "uint256[]"
  }, {
    name: "amounts",
    type: "uint256[]"
  }, {
    name: "data",
    type: "bytes"
  }],
  outputs: []
}] as const;
const MANIFOLD_CORE_ABI = [{
  type: "function",
  name: "tokenExtension",
  stateMutability: "view",
  inputs: [{
    name: "tokenId",
    type: "uint256"
  }],
  outputs: [{
    type: "address"
  }]
}] as const;
type FlowState = "review" | "submission";
type TxState = "pending" | "awaiting_approval" | "submitted" | "success" | "error";
type TxEntry = {
  id: string;
  originKey: string;
  label: string;
  state: TxState;
  hash?: `0x${string}`;
  error?: string;
};
type TxItem = {
  key: string;
  tokenId: bigint;
  qty: bigint;
  contractType: ContractType;
  contract: Address;
};
type WalletClientWithWrite = {
  writeContract: (req: WriteContractParameters) => Promise<`0x${string}`>;
};
const waitForPaint = async () => {
  if (!globalThis.window) return;
  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
};
function hasWriteContract(client: any): client is WalletClientWithWrite {
  return client && typeof client.writeContract === "function";
}
function computeFlowTitle(total: number, successCount: number, errorCount: number): {
  label: string;
  icon: string;
} {
  const allSuccess = total > 0 && successCount === total;
  const allFail = total > 0 && errorCount === total;
  if (total === 1 && allSuccess) {
    return {
      label: "Transfer Successful",
      icon: "/emojis/sgt_saluting_face.webp"
    };
  }
  if (total === 1 && allFail) {
    return {
      label: "Transfer Failed",
      icon: "/emojis/sgt_sob.webp"
    };
  }
  if (total === 1) {
    return {
      label: "Transfer Complete",
      icon: "/emojis/sgt_grimacing.webp"
    };
  }
  if (allSuccess) {
    return {
      label: `All ${total} Transactions Successful`,
      icon: "/emojis/sgt_saluting_face.webp"
    };
  }
  if (allFail) {
    return {
      label: `All ${total} Transactions Failed`,
      icon: "/emojis/sgt_sob.webp"
    };
  }
  return {
    label: `Transfer Complete: ${successCount} successful, ${errorCount} failed`,
    icon: "/emojis/sgt_grimacing.webp"
  };
}
function FlowTitle(t0) {
  const $ = _c(17);
  const {
    flow,
    txs
  } = t0;
  if (flow === "review") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span>Review transfer and select recipient</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const anyPending = anyTxsPending(txs);
  if (anyPending) {
    const t1 = txs.length > 1 ? "s" : "";
    let t2;
    if ($[1] !== t1 || $[2] !== txs.length) {
      t2 = <span>Executing {txs.length} Transaction{t1}</span>;
      $[1] = t1;
      $[2] = txs.length;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <CircleLoader size={CircleLoaderSize.MEDIUM} />;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t2) {
      t4 = <span className="tw-flex tw-items-center tw-gap-1.5">{t2}{t3}</span>;
      $[5] = t2;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  const total = txs.length;
  let t1;
  if ($[7] !== total || $[8] !== txs) {
    const successCount = txs.filter(_temp).length;
    const errorCount = txs.filter(_temp2).length;
    t1 = computeFlowTitle(total, successCount, errorCount);
    $[7] = total;
    $[8] = txs;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const {
    label,
    icon
  } = t1;
  let t2;
  if ($[10] !== label) {
    t2 = <span>{label}</span>;
    $[10] = label;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== icon) {
    t3 = <img src={icon} alt="status" className="tw-w-6 tw-h-6" />;
    $[12] = icon;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t2 || $[15] !== t3) {
    t4 = <span className="tw-flex tw-items-center tw-gap-1.5">{t2}{t3}</span>;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}
function _temp2(t_0) {
  return t_0.state === "error";
}
function _temp(t) {
  return t.state === "success";
}
function SelectedSummaryList(t0) {
  const $ = _c(23);
  const {
    items,
    leftListRef,
    leftHasOverflow,
    leftAtEnd
  } = t0;
  let t1;
  if ($[0] !== items.length) {
    t1 = <span className="tw-font-bold">{items.length}</span>;
    $[0] = items.length;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = items.length === 1 ? "NFT" : "NFTs";
  let t3;
  if ($[2] !== items) {
    t3 = items.reduce(_temp3, 0);
    $[2] = items;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <span className="tw-font-bold">{t3}</span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = items.reduce(_temp4, 0) === 1 ? "item" : "items";
  let t6;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t4 || $[9] !== t5) {
    t6 = <div className="tw-font-semibold tw-flex-shrink-0">You're transferring {t1}{" "}{t2} ·{" "}{t4}{" "}{t5}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== items) {
    t7 = items.map(_temp5);
    $[11] = items;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== leftListRef || $[14] !== t7) {
    t8 = <ul ref={leftListRef} className="tw-flex-1 tw-min-h-0 tw-overflow-auto tw-space-y-2 tw-pl-0 tw-pr-3 tw-[scrollbar-gutter:stable] tw-scrollbar-thin tw-scrollbar-thumb-white/30 tw-scrollbar-track-transparent hover:tw-scrollbar-thumb-white/50">{t7}</ul>;
    $[13] = leftListRef;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== leftAtEnd || $[17] !== leftHasOverflow) {
    t9 = leftHasOverflow && <div className="tw-text-xs tw-opacity-75 tw-text-center tw-flex-shrink-0"><FontAwesomeIcon icon={leftAtEnd ? faAnglesUp : faAnglesDown} />{" "}Scroll for more</div>;
    $[16] = leftAtEnd;
    $[17] = leftHasOverflow;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t6 || $[20] !== t8 || $[21] !== t9) {
    t10 = <div className="tw-flex tw-flex-col tw-space-y-2 tw-min-h-0 tw-max-h-full tw-overflow-hidden">{t6}{t8}{t9}</div>;
    $[19] = t6;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function _temp5(it_1) {
  const [collection, tokenId] = it_1.key.split(":");
  return <li key={it_1.key} className="tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-bg-white/10 tw-p-2">{it_1.thumbUrl ? <div className="tw-relative tw-h-10 tw-w-10 tw-rounded-md tw-overflow-hidden tw-bg-white/10"><Image alt={it_1.title ?? it_1.key} src={it_1.thumbUrl} fill={true} sizes="40px" className="tw-object-contain" quality={90} /></div> : <div className="tw-h-10 tw-w-10 tw-rounded-md tw-bg-white/10" />}<div className="tw-min-w-0 tw-flex-1"><div className="tw-truncate tw-text-sm">{it_1.title ?? `${collection} #${tokenId}`}</div><div className="tw-truncate tw-text-xs tw-opacity-70">{collection} #{tokenId}</div></div><div className="tw-text-xs tw-font-medium">x{it_1.qty}</div></li>;
}
function _temp4(sum_0, it_0) {
  return sum_0 + (it_0.qty || 0);
}
function _temp3(sum, it) {
  return sum + (it.qty || 0);
}
function RecipientSelected(t0) {
  const $ = _c(51);
  const {
    selectedProfile,
    profile,
    isIdentityLoading,
    setSelectedProfile,
    setSelectedWallet,
    setResults,
    setQuery,
    searchInputRef,
    walletsListRef,
    walletsHasOverflow,
    walletsAtEnd,
    selectedWallet
  } = t0;
  let t1;
  if ($[0] !== profile?.wallets) {
    t1 = profile?.wallets ?? [];
    $[0] = profile?.wallets;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const wallets = t1;
  let walletsContent;
  if (isIdentityLoading) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="tw-text-xs tw-opacity-60">Loading wallets…</div>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    walletsContent = t2;
  } else {
    if (wallets.length === 0) {
      const t2 = selectedProfile.display || selectedProfile.handle;
      let t3;
      if ($[3] !== t2) {
        t3 = <div className="tw-text-xs tw-opacity-60">No wallets found for {t2}.</div>;
        $[3] = t2;
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      walletsContent = t3;
    } else {
      let t2;
      if ($[5] !== selectedWallet || $[6] !== setSelectedWallet || $[7] !== wallets) {
        let t3;
        if ($[9] !== selectedWallet || $[10] !== setSelectedWallet) {
          t3 = w => {
            const isSel = selectedWallet?.toLowerCase() === w.wallet.toLowerCase();
            return <button key={w.wallet} type="button" onClick={() => setSelectedWallet(w.wallet)} className={["tw-w-full tw-rounded-lg tw-border tw-border-white/10 tw-bg-white/10 hover:tw-bg-white/15 tw-p-2 tw-flex tw-flex-col tw-items-start tw-justify-between", isSel ? "tw-border-2 tw-border-solid !tw-border-emerald-400" : ""].join(" ")}><div className="tw-text-sm tw-font-medium">{w.display || w.wallet}</div><div className="tw-text-[11px] tw-opacity-60">{w.wallet}</div></button>;
          };
          $[9] = selectedWallet;
          $[10] = setSelectedWallet;
          $[11] = t3;
        } else {
          t3 = $[11];
        }
        t2 = wallets.map(t3);
        $[5] = selectedWallet;
        $[6] = setSelectedWallet;
        $[7] = wallets;
        $[8] = t2;
      } else {
        t2 = $[8];
      }
      walletsContent = t2;
    }
  }
  const t2 = selectedProfile.display || selectedProfile.handle || selectedProfile.wallet;
  let t3;
  if ($[12] !== selectedProfile.level || $[13] !== selectedProfile.pfp || $[14] !== t2) {
    t3 = <TransferModalPfp src={selectedProfile.pfp} alt={t2} level={selectedProfile.level} />;
    $[12] = selectedProfile.level;
    $[13] = selectedProfile.pfp;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const t4 = selectedProfile.display || selectedProfile.handle;
  let t5;
  if ($[16] !== t4) {
    t5 = <div className="tw-truncate tw-text-sm tw-font-medium">{t4}</div>;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== selectedProfile.tdh) {
    t6 = selectedProfile.tdh.toLocaleString();
    $[18] = selectedProfile.tdh;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== selectedProfile.level || $[21] !== t6) {
    t7 = <div className="tw-truncate tw-text-[11px] tw-opacity-60">TDH: {t6} - Level:{" "}{selectedProfile.level}</div>;
    $[20] = selectedProfile.level;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t5 || $[24] !== t7) {
    t8 = <div className="tw-min-w-0">{t5}{t7}</div>;
    $[23] = t5;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t3 || $[27] !== t8) {
    t9 = <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">{t3}{t8}</div>;
    $[26] = t3;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== searchInputRef || $[30] !== setQuery || $[31] !== setResults || $[32] !== setSelectedProfile || $[33] !== setSelectedWallet) {
    t10 = <button type="button" className="!tw-text-xs tw-rounded-md tw-bg-white/10 hover:tw-bg-white/15 tw-px-2 tw-py-1 tw-border-2 tw-border-solid tw-border-[#444] tw-font-medium" onClick={() => {
      setSelectedProfile(null);
      setSelectedWallet(null);
      setResults([]);
      setQuery("");
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }}>Change</button>;
    $[29] = searchInputRef;
    $[30] = setQuery;
    $[31] = setResults;
    $[32] = setSelectedProfile;
    $[33] = setSelectedWallet;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t9) {
    t11 = <div className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-bg-white/10 tw-px-3 tw-py-2">{t9}{t10}</div>;
    $[35] = t10;
    $[36] = t9;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="tw-text-sm">Choose destination wallet</div>;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== walletsContent || $[40] !== walletsListRef) {
    t13 = <div ref={walletsListRef} className="tw-space-y-2 tw-flex-1 tw-min-h-0 tw-overflow-auto tw-pr-3 tw-[scrollbar-gutter:stable] tw-scrollbar-thin tw-scrollbar-thumb-white/30 tw-scrollbar-track-transparent hover:tw-scrollbar-thumb-white/50">{walletsContent}</div>;
    $[39] = walletsContent;
    $[40] = walletsListRef;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== walletsAtEnd || $[43] !== walletsHasOverflow) {
    t14 = walletsHasOverflow && <div className="tw-text-xs tw-opacity-75 tw-text-center"><FontAwesomeIcon icon={walletsAtEnd ? faAnglesUp : faAnglesDown} />{" "}Scroll for more</div>;
    $[42] = walletsAtEnd;
    $[43] = walletsHasOverflow;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t13 || $[46] !== t14) {
    t15 = <div className="tw-pt-4 tw-space-y-2 tw-flex tw-flex-col tw-min-h-0">{t12}{t13}{t14}</div>;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t11 || $[49] !== t15) {
    t16 = <>{t11}{t15}</>;
    $[48] = t11;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  return t16;
}
function RecipientSearch(t0) {
  const $ = _c(24);
  const {
    query,
    setQuery,
    searchStatusText,
    results,
    onPick,
    resultsListRef,
    resultsHasOverflow,
    resultsAtEnd,
    searchInputRef
  } = t0;
  let t1;
  if ($[0] !== setQuery) {
    t1 = e => setQuery(e.target.value);
    $[0] = setQuery;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== query || $[3] !== searchInputRef || $[4] !== t1) {
    t2 = <input autoFocus={true} type="text" value={query} onChange={t1} placeholder="Search by handle, ens or wallet" className="tw-h-14 tw-w-full tw-rounded-lg tw-border-none tw-bg-white/10 tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-bg-white/20" ref={searchInputRef} />;
    $[2] = query;
    $[3] = searchInputRef;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== searchStatusText) {
    t3 = <div className="tw-text-[12px] tw-opacity-60">{searchStatusText}</div>;
    $[6] = searchStatusText;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== onPick || $[9] !== results) {
    let t5;
    if ($[11] !== onPick) {
      t5 = r => <button key={r.profile_id ?? r.wallet} type="button" onClick={() => onPick(r)} className="tw-w-full tw-text-left tw-rounded-lg tw-border tw-border-white/10 tw-bg-white/10 hover:tw-bg-white/15 tw-p-2 tw-flex tw-items-center tw-gap-3"><TransferModalPfp src={r.pfp} alt={r.display || r.handle || r.wallet} level={r.level} /><div className="tw-min-w-0 tw-flex-1"><div className="tw-truncate tw-text-sm tw-font-medium">{r.display || r.handle}</div><div className="tw-truncate tw-text-[11px] tw-opacity-60">TDH: {r.tdh.toLocaleString()} - Level: {r.level}</div></div></button>;
      $[11] = onPick;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t4 = results.map(t5);
    $[8] = onPick;
    $[9] = results;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[13] !== resultsListRef || $[14] !== t4) {
    t5 = <div ref={resultsListRef} className="tw-space-y-2 tw-flex-1 tw-min-h-0 tw-overflow-auto tw-pr-3 tw-[scrollbar-gutter:stable] tw-scrollbar-thin tw-scrollbar-thumb-white/30 tw-scrollbar-track-transparent hover:tw-scrollbar-thumb-white/50">{t4}</div>;
    $[13] = resultsListRef;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== resultsAtEnd || $[17] !== resultsHasOverflow) {
    t6 = resultsHasOverflow && <div className="tw-text-xs tw-opacity-75 tw-text-center"><FontAwesomeIcon icon={resultsAtEnd ? faAnglesUp : faAnglesDown} />{" "}Scroll for more</div>;
    $[16] = resultsAtEnd;
    $[17] = resultsHasOverflow;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t2 || $[20] !== t3 || $[21] !== t5 || $[22] !== t6) {
    t7 = <>{t2}{t3}{t5}{t6}</>;
    $[19] = t2;
    $[20] = t3;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}
function TxStatusList(t0) {
  const $ = _c(9);
  const {
    txs,
    publicClient
  } = t0;
  const explorer = publicClient?.chain?.blockExplorers?.default.url;
  const getBgColor = _temp6;
  const getTextColor = _temp7;
  let t1;
  if ($[0] !== explorer) {
    t1 = hash => {
      if (!explorer) {
        return null;
      }
      return <Link href={`${explorer}/tx/${hash}`} target="_blank" rel="noreferrer" className="tw-inline-block tw-no-underline tw-ml-2 !tw-text-sm tw-rounded-md tw-bg-white hover:tw-bg-white/80 tw-text-black hover:tw-text-black tw-px-2 tw-py-1 tw-border tw-border-solid tw-border-black">View Tx</Link>;
    };
    $[0] = explorer;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const txLink = t1;
  let t2;
  if ($[2] !== txLink || $[3] !== txs) {
    let t3;
    if ($[5] !== txLink) {
      t3 = (t, index) => <div key={t.id} className="tw-rounded-lg tw-p-4" style={{
        backgroundColor: getBgColor(t.state),
        color: getTextColor(t.state)
      }}><div className="tw-font-medium">{index + 1}/ {t.label}</div><div className="tw-text-xs tw-opacity-60">Originator: {t.originKey}</div><div className="tw-text-sm tw-mt-2">{t.state === "pending" && <span>Pending</span>}{t.state === "awaiting_approval" && <span>Approve in your wallet</span>}{t.state === "error" && <span>Error: {t.error || "Transaction failed"}{t.hash && txLink(t.hash)}</span>}{t.state === "submitted" && <span>Submitted — waiting for confirmation{t.hash && txLink(t.hash)}</span>}{t.state === "success" && <span>Successful{t.hash && txLink(t.hash)}</span>}</div></div>;
      $[5] = txLink;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = txs.map(t3);
    $[2] = txLink;
    $[3] = txs;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <>{t2}</>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

// --- Helper functions/components to offload branching logic ---
function _temp7(state_0) {
  if (state_0 === "awaiting_approval") {
    return "#fff";
  }
  return "#000";
}
function _temp6(state) {
  switch (state) {
    case "awaiting_approval":
      {
        return "#406AFE";
      }
    case "submitted":
      {
        return "#a1e1ff";
      }
    case "error":
      {
        return "#ffcccc";
      }
    case "success":
      {
        return "#ccffcc";
      }
    default:
      {
        return "rgba(255, 255, 255, 0.9)";
      }
  }
}
function getSearchStatusText(isSearching: boolean, needsMoreSearchCharacters: boolean, remainingSearchCharacters: number, resultsLen: number, trimmedQuery: string): string {
  if (isSearching) return "Searching…";
  if (needsMoreSearchCharacters) {
    const plural = remainingSearchCharacters === 1 ? "" : "s";
    return `Type ${remainingSearchCharacters} more character${plural}`;
  }
  if (resultsLen > 0) {
    const plural = resultsLen === 1 ? "" : "s";
    return `Found ${resultsLen} result${plural}`;
  }
  if (trimmedQuery) return "No results.";
  return "Type to search.";
}
function HeaderRight(t0) {
  const $ = _c(6);
  const {
    flow,
    trxPending,
    onClose
  } = t0;
  let t1;
  if ($[0] !== flow || $[1] !== onClose || $[2] !== trxPending) {
    t1 = (flow === "review" || !trxPending) && <button type="button" onClick={onClose} aria-label="Close" className="tw-bg-transparent tw-border-none tw-p-0 tw-flex tw-items-center tw-justify-center"><FontAwesomeIcon icon={faXmarkCircle} className="tw-size-6" /></button>;
    $[0] = flow;
    $[1] = onClose;
    $[2] = trxPending;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <div className="tw-flex tw-items-center tw-gap-3">{t1}</div>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function FooterActions(t0) {
  const $ = _c(11);
  const {
    flow,
    canConfirm,
    onCancel,
    onConfirm,
    onClose,
    txs
  } = t0;
  if (flow === "review") {
    let t1;
    if ($[0] !== onCancel) {
      t1 = <button type="button" onClick={onCancel} className="tw-rounded-lg tw-bg-white/10 hover:tw-bg-white/15 tw-px-4 tw-py-2 tw-border-2 tw-border-solid tw-border-[#444] tw-font-medium">Cancel</button>;
      $[0] = onCancel;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const t2 = !canConfirm;
    let t3;
    if ($[2] !== onConfirm || $[3] !== t2) {
      t3 = <button type="button" disabled={t2} onClick={onConfirm} className="tw-rounded-lg tw-bg-white tw-text-black tw-px-4 tw-py-2 tw-border-2 tw-border-solid tw-border-[#444] disabled:tw-opacity-60 disabled:tw-cursor-not-allowed tw-font-medium">Transfer</button>;
      $[2] = onConfirm;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t1 || $[6] !== t3) {
      t4 = <div className="tw-flex tw-items-center tw-gap-2">{t1}{t3}</div>;
      $[5] = t1;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  const anyPending = anyTxsPending(txs);
  if (anyPending) {
    let t1;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <button type="button" disabled={true} className="tw-rounded-lg tw-bg-white/10 tw-px-4 tw-py-2 tw-opacity-60 tw-font-medium">Processing…</button>;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    return t1;
  }
  let t1;
  if ($[9] !== onClose) {
    t1 = <button type="button" onClick={onClose} className="tw-rounded-lg tw-bg-white hover:tw-bg-white/80 tw-text-black hover:tw-text-black tw-px-4 tw-py-2">Close</button>;
    $[9] = onClose;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
function BodyByFlow(t0) {
  const $ = _c(42);
  const {
    flow,
    items,
    leftListRef,
    leftHasOverflow,
    leftAtEnd,
    selectedProfile,
    profile,
    isIdentityLoading,
    setSelectedProfile,
    setSelectedWallet,
    setResults,
    setQuery,
    searchInputRef,
    walletsListRef,
    walletsHasOverflow,
    walletsAtEnd,
    selectedWallet,
    query,
    setQueryExternal,
    searchStatusText,
    results,
    onPick,
    resultsListRef,
    resultsHasOverflow,
    resultsAtEnd,
    publicClient,
    txs
  } = t0;
  if (flow === "submission") {
    const anyPending = anyTxsPending(txs);
    const t1 = anyPending ? "Follow the prompts in your wallet and keep this tab open." : "All transactions have been completed. You can close this window now.";
    let t2;
    if ($[0] !== t1) {
      t2 = <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs sm:tw-text-sm tw-opacity-80"><span>{t1}</span></div>;
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== publicClient || $[3] !== txs) {
      t3 = <TxStatusList txs={txs} publicClient={publicClient} />;
      $[2] = publicClient;
      $[3] = txs;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t2 || $[6] !== t3) {
      t4 = <div className="tw-flex-1 tw-overflow-auto tw-p-4 sm:tw-p-6 tw-space-y-4">{t2}{t3}</div>;
      $[5] = t2;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t1;
  if ($[8] !== items || $[9] !== leftAtEnd || $[10] !== leftHasOverflow || $[11] !== leftListRef) {
    t1 = <div className="tw-min-h-0 tw-max-h-[50%] lg:tw-max-h-none"><SelectedSummaryList items={items} leftListRef={leftListRef} leftHasOverflow={leftHasOverflow} leftAtEnd={leftAtEnd} /></div>;
    $[8] = items;
    $[9] = leftAtEnd;
    $[10] = leftHasOverflow;
    $[11] = leftListRef;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const t2 = `tw-font-semibold ${selectedProfile ? "tw-mb-2" : "tw-mb-0"}`;
  let t3;
  if ($[13] !== t2) {
    t3 = <div className={t2}>Recipient</div>;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== isIdentityLoading || $[16] !== onPick || $[17] !== profile || $[18] !== query || $[19] !== results || $[20] !== resultsAtEnd || $[21] !== resultsHasOverflow || $[22] !== resultsListRef || $[23] !== searchInputRef || $[24] !== searchStatusText || $[25] !== selectedProfile || $[26] !== selectedWallet || $[27] !== setQuery || $[28] !== setQueryExternal || $[29] !== setResults || $[30] !== setSelectedProfile || $[31] !== setSelectedWallet || $[32] !== walletsAtEnd || $[33] !== walletsHasOverflow || $[34] !== walletsListRef) {
    t4 = selectedProfile ? <RecipientSelected selectedProfile={selectedProfile} profile={profile} isIdentityLoading={isIdentityLoading} setSelectedProfile={setSelectedProfile} setSelectedWallet={setSelectedWallet} setResults={setResults} setQuery={setQuery} searchInputRef={searchInputRef} walletsListRef={walletsListRef} walletsHasOverflow={walletsHasOverflow} walletsAtEnd={walletsAtEnd} selectedWallet={selectedWallet} /> : <RecipientSearch query={query} setQuery={setQueryExternal} searchStatusText={searchStatusText} results={results} onPick={onPick} resultsListRef={resultsListRef} resultsHasOverflow={resultsHasOverflow} resultsAtEnd={resultsAtEnd} searchInputRef={searchInputRef} />;
    $[15] = isIdentityLoading;
    $[16] = onPick;
    $[17] = profile;
    $[18] = query;
    $[19] = results;
    $[20] = resultsAtEnd;
    $[21] = resultsHasOverflow;
    $[22] = resultsListRef;
    $[23] = searchInputRef;
    $[24] = searchStatusText;
    $[25] = selectedProfile;
    $[26] = selectedWallet;
    $[27] = setQuery;
    $[28] = setQueryExternal;
    $[29] = setResults;
    $[30] = setSelectedProfile;
    $[31] = setSelectedWallet;
    $[32] = walletsAtEnd;
    $[33] = walletsHasOverflow;
    $[34] = walletsListRef;
    $[35] = t4;
  } else {
    t4 = $[35];
  }
  let t5;
  if ($[36] !== t3 || $[37] !== t4) {
    t5 = <div className="tw-flex tw-flex-col tw-space-y-2 tw-min-h-0 tw-flex-1 lg:tw-flex-initial">{t3}{t4}</div>;
    $[36] = t3;
    $[37] = t4;
    $[38] = t5;
  } else {
    t5 = $[38];
  }
  let t6;
  if ($[39] !== t1 || $[40] !== t5) {
    t6 = <div className="tw-flex-1 tw-overflow-hidden tw-flex tw-flex-col lg:tw-grid lg:tw-grid-cols-2 tw-gap-6 tw-px-4 tw-py-2">{t1}{t5}</div>;
    $[39] = t1;
    $[40] = t5;
    $[41] = t6;
  } else {
    t6 = $[41];
  }
  return t6;
}
function anyTxsPending(txs: TxEntry[]) {
  return txs.some(t => t.state === "pending" || t.state === "awaiting_approval" || t.state === "submitted");
}
export default function TransferModal({
  open,
  onClose
}: {
  readonly open: boolean;
  readonly onClose: (opts?: {
    completed?: boolean;
  }) => void;
}) {
  const t = useTransfer();
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<CommunityMemberMinimal[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<CommunityMemberMinimal | null>(null);
  const handleOrWallet = selectedProfile?.handle ?? selectedProfile?.wallet ?? null;
  const {
    profile,
    isLoading: isIdentityLoading
  } = useIdentity({
    handleOrWallet: handleOrWallet ?? "",
    initialProfile: null
  });
  const [selectedWallet, setSelectedWallet] = useState<string | null>(null);
  const leftListRef = useRef<HTMLUListElement | null>(null);
  const resultsListRef = useRef<HTMLDivElement | null>(null);
  const walletsListRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [leftHasOverflow, setLeftHasOverflow] = useState(false);
  const [resultsHasOverflow, setResultsHasOverflow] = useState(false);
  const [walletsHasOverflow, setWalletsHasOverflow] = useState(false);
  const [leftAtEnd, setLeftAtEnd] = useState(false);
  const [resultsAtEnd, setResultsAtEnd] = useState(false);
  const [walletsAtEnd, setWalletsAtEnd] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [flow, setFlow] = useState<FlowState>("review");
  const [txs, setTxs] = useState<TxEntry[]>([]);
  const trxPending = useMemo(() => anyTxsPending(txs), [txs]);
  const publicClient = usePublicClient();
  const {
    data: walletClient
  } = useWalletClient();
  const {
    address
  } = useAccount();
  const trimmedQuery = query.trim();
  const needsMoreSearchCharacters = trimmedQuery.length > 0 && trimmedQuery.length < MIN_SEARCH_LENGTH;
  const remainingSearchCharacters = Math.max(MIN_SEARCH_LENGTH - trimmedQuery.length, 0);
  const groupByContractAndOriginator = useCallback(async (publicClient_0: PublicClient, items: TxItem[]) => {
    const by = new Map<string, {
      contract: string;
      collection: string;
      originKey: string;
      is1155: boolean;
      items: TxItem[];
    }>();
    const getOriginKey = async (contract: Address, tokenId: bigint) => {
      try {
        const ext = (await publicClient_0.readContract({
          address: contract,
          abi: MANIFOLD_CORE_ABI,
          functionName: "tokenExtension",
          args: [tokenId]
        })) as Address;
        return `ext:${ext.toLowerCase()}`;
      } catch {
        // core-minted (no extension for token)
        return `core:${contract.toLowerCase()}`;
      }
    };
    for (const it of items) {
      const contract_0 = (it.contract as string).toLowerCase();
      const is1155 = it.contractType === ContractType.ERC1155;
      let originKey = "erc721";
      if (is1155) {
        const id = BigInt(it.tokenId);
        originKey = await getOriginKey(contract_0 as Address, id);
      }
      const key = `${contract_0}::${originKey}`;
      const existing = by.get(key);
      if (existing) {
        existing.items.push(it);
      } else {
        by.set(key, {
          contract: contract_0,
          collection: it.key.split(":")[0],
          originKey,
          is1155,
          items: [it]
        });
      }
    }
    return by;
  }, []);

  // --- lifecycle & effects ---
  useEffect(() => {
    if (!open) {
      setQuery("");
      setIsSearching(false);
      setResults([]);
      setSelectedProfile(null);
      setSelectedWallet(null);
      setFlow("review");
      setLeftHasOverflow(false);
      setResultsHasOverflow(false);
      setWalletsHasOverflow(false);
      setLeftAtEnd(false);
      setResultsAtEnd(false);
      setWalletsAtEnd(false);
      setIsClosing(false);
    }
  }, [open]);
  useEffect(() => {
    if (open) setIsClosing(false);
  }, [open]);

  // lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    setSelectedProfile(null);
    setSelectedWallet(null);
  }, [open]);
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);
  const debouncedQuery = useDebouncedValue(trimmedQuery, 350);
  const enabled = open && debouncedQuery.length >= MIN_SEARCH_LENGTH;
  const {
    data,
    isFetching
  } = useQuery<CommunityMemberMinimal[]>({
    queryKey: ["memberSearch", debouncedQuery],
    enabled,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 30_000,
    gcTime: 300_000,
    queryFn: async ({
      signal
    }: {
      signal: AbortSignal;
    }) => {
      try {
        const arr = await commonApiFetch<CommunityMemberMinimal[]>({
          endpoint: "community-members",
          params: {
            param: debouncedQuery
          },
          signal
        });
        if (arr && arr.length > 0) return arr;
        const identity = await getUserProfile({
          user: debouncedQuery,
          headers: {}
        });
        const communityMember: CommunityMemberMinimal = {
          profile_id: identity.id,
          handle: identity.handle,
          wallet: identity.primary_wallet,
          pfp: identity.pfp,
          display: identity.handle ?? null,
          normalised_handle: identity.normalised_handle,
          primary_wallet: identity.primary_wallet,
          tdh: identity.tdh,
          level: identity.level,
          cic_rating: identity.cic
        };
        return [communityMember];
      } catch {
        return [];
      }
    }
  });
  useEffect(() => {
    const preDebounce = open && trimmedQuery.length >= MIN_SEARCH_LENGTH && debouncedQuery !== trimmedQuery;
    setIsSearching(preDebounce || enabled && isFetching);
  }, [open, trimmedQuery, debouncedQuery, enabled, isFetching]);
  useEffect(() => {
    if (!enabled) {
      setResults([]);
      return;
    }
    setResults(data ?? []);
  }, [enabled, data, setResults]);
  const items_0 = useMemo(() => Array.from(t.selected.values()).map(it_0 => ({
    key: it_0.key,
    qty: Math.min(Math.max(1, it_0.qty ?? 1), Math.max(1, it_0.max ?? 1)),
    title: it_0.title,
    thumbUrl: it_0.thumbUrl
  })), [t.selected]);
  useEffect(() => {
    const hasOverflow = (el: HTMLElement | null) => !!el && el.scrollHeight > el.clientHeight;
    const check = () => {
      setLeftHasOverflow(hasOverflow(leftListRef.current));
      setResultsHasOverflow(hasOverflow(resultsListRef.current));
      setWalletsHasOverflow(hasOverflow(walletsListRef.current));
      const nearBottom = (el_0: HTMLElement | null) => {
        if (!el_0) return false;
        const threshold = 4;
        return el_0.scrollTop + el_0.clientHeight >= el_0.scrollHeight - threshold;
      };
      setLeftAtEnd(nearBottom(leftListRef.current));
      setResultsAtEnd(nearBottom(resultsListRef.current));
      setWalletsAtEnd(nearBottom(walletsListRef.current));
    };
    check();
    globalThis.addEventListener("resize", check);
    const leftEl = leftListRef.current;
    const resultsEl = resultsListRef.current;
    const walletsEl = walletsListRef.current;
    const onScrollLeft = () => {
      const el_1 = leftListRef.current;
      if (!el_1) return;
      const threshold_0 = 4;
      setLeftAtEnd(el_1.scrollTop + el_1.clientHeight >= el_1.scrollHeight - threshold_0);
    };
    const onScrollResults = () => {
      const el_2 = resultsListRef.current;
      if (!el_2) return;
      const threshold_1 = 4;
      setResultsAtEnd(el_2.scrollTop + el_2.clientHeight >= el_2.scrollHeight - threshold_1);
    };
    const onScrollWallets = () => {
      const el_3 = walletsListRef.current;
      if (!el_3) return;
      const threshold_2 = 4;
      setWalletsAtEnd(el_3.scrollTop + el_3.clientHeight >= el_3.scrollHeight - threshold_2);
    };
    leftEl?.addEventListener("scroll", onScrollLeft);
    resultsEl?.addEventListener("scroll", onScrollResults);
    walletsEl?.addEventListener("scroll", onScrollWallets);
    return () => {
      globalThis.removeEventListener("resize", check);
      leftEl?.removeEventListener("scroll", onScrollLeft);
      resultsEl?.removeEventListener("scroll", onScrollResults);
      walletsEl?.removeEventListener("scroll", onScrollWallets);
    };
  }, [open, items_0, results, profile?.wallets, isIdentityLoading]);
  const totalUnits = t.totalQty;
  const canConfirm = open && selectedWallet && totalUnits > 0;
  const handleClose = useCallback(() => {
    if (trxPending) return;
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose({
        completed: flow !== "review" && txs.length > 0 && !txs.every(t_0 => t_0.state === "error")
      });
    }, 150);
  }, [isClosing, onClose, flow, trxPending, txs]);
  const handleConfirm = useCallback(async () => {
    if (!publicClient || !address) {
      setTxs([{
        id: "init",
        originKey: "init",
        label: "Client not ready",
        state: "error",
        error: "Client not ready. Please reconnect."
      }]);
      setFlow("submission");
      return;
    }
    const selItems: TxItem[] = Array.from(t.selected.values()).map(it_1 => ({
      contract: it_1.contract as Address,
      tokenId: BigInt(it_1.tokenId),
      qty: BigInt(Math.min(Math.max(1, it_1.qty ?? 1), Math.max(1, it_1.max ?? 1))),
      key: it_1.key,
      contractType: it_1.contractType
    }));
    const byContract = await groupByContractAndOriginator(publicClient, selItems);
    for (const value of Array.from(byContract.values())) {
      value.items.sort((a, b) => a.tokenId.toString().localeCompare(b.tokenId.toString()));
    }
    const initial: TxEntry[] = [];
    for (const [key_0, {
      collection,
      originKey: originKey_0,
      contract: contract_1,
      is1155: is1155_0,
      items: citems
    }] of Array.from(byContract.entries())) {
      if (is1155_0) {
        const itemsLabel = citems.map((x: TxItem) => {
          const tokenId_0 = x.tokenId.toString();
          const count = x.qty;
          return `#${tokenId_0}(x${count})`;
        }).join(" - ");
        initial.push({
          id: `${key_0}-batch`,
          originKey: originKey_0,
          label: `${collection} ${itemsLabel}`,
          state: "pending"
        });
      } else {
        for (const x_0 of citems) {
          initial.push({
            id: `${contract_1}-${x_0.tokenId.toString()}`,
            originKey: originKey_0,
            label: `${collection} #${x_0.tokenId.toString()}`,
            state: "pending"
          });
        }
      }
    }
    setTxs(initial);
    setFlow("submission");
    if (!walletClient) {
      setTxs([{
        id: "init",
        originKey: "init",
        label: "Wallet not ready",
        state: "error",
        error: "Wallet not ready. Please reconnect."
      }]);
      return;
    }
    if (!selectedWallet || !isAddress(selectedWallet)) {
      setTxs([{
        id: "init",
        originKey: "init",
        label: "Invalid destination wallet",
        state: "error",
        error: "Invalid destination wallet. Please choose another wallet."
      }]);
      return;
    }
    if (!hasWriteContract(walletClient)) {
      setTxs([{
        id: "init",
        originKey: "init",
        label: "Wallet not ready",
        state: "error",
        error: "Wallet does not support writeContract."
      }]);
      return;
    }
    const destinationWallet = selectedWallet as Address;
    const write = walletClient.writeContract;
    for (const [key_1, {
      contract: contract_2,
      is1155: is1155_1,
      items: citems_0
    }] of Array.from(byContract.entries())) {
      if (is1155_1) {
        // Try batch
        try {
          const ids = citems_0.map((x_1: TxItem) => x_1.tokenId);
          const amts = citems_0.map((x_2: TxItem) => x_2.qty);
          const {
            request
          } = await publicClient.simulateContract({
            account: address,
            address: contract_2 as Address,
            abi: ERC1155_ABI,
            functionName: "safeBatchTransferFrom",
            args: [address as Address, destinationWallet, ids, amts, "0x"]
          });
          setTxs(prev_1 => prev_1.map(e_0 => e_0.id === `${key_1}-batch` ? {
            ...e_0,
            state: "awaiting_approval"
          } : e_0));
          const hash = await write(request);
          setTxs(prev_2 => prev_2.map(e_1 => e_1.id === `${key_1}-batch` ? {
            ...e_1,
            state: "submitted",
            hash
          } : e_1));
          await waitForPaint();
          const receipt = await publicClient.waitForTransactionReceipt({
            hash
          });
          setTxs(prev_3 => prev_3.map(e_2 => e_2.id === `${key_1}-batch` ? {
            ...e_2,
            state: receipt.status === "success" ? "success" : "error"
          } : e_2));
          await waitForPaint();
        } catch (e: any) {
          console.error("Error in batch 1155 transfer", e);
          setTxs(prev_0 => prev_0.map(te => te.id === `${key_1}-batch` ? {
            ...te,
            state: "error",
            error: String(e?.shortMessage || e?.message || e)
          } : te));
        }
      } else {
        // ERC721 one by one
        for (const x_3 of citems_0) {
          const tid = `${contract_2}-${x_3.tokenId.toString()}`;
          try {
            const {
              request: request_0
            } = await publicClient.simulateContract({
              account: address,
              address: contract_2 as Address,
              abi: ERC721_ABI,
              functionName: "safeTransferFrom",
              args: [address as Address, destinationWallet, x_3.tokenId]
            });
            setTxs(prev_5 => prev_5.map(te_1 => te_1.id === tid ? {
              ...te_1,
              state: "awaiting_approval"
            } : te_1));
            try {
              const hash_0 = await write(request_0);
              setTxs(prev_7 => prev_7.map(te_3 => te_3.id === tid ? {
                ...te_3,
                state: "submitted",
                hash: hash_0
              } : te_3));
              await waitForPaint();
              const receipt_0 = await publicClient.waitForTransactionReceipt({
                hash: hash_0
              });
              setTxs(prev_8 => prev_8.map(te_4 => te_4.id === tid ? {
                ...te_4,
                state: receipt_0.status === "success" ? "success" : "error"
              } : te_4));
              await waitForPaint();
            } catch (e_3: any) {
              console.error("Error in 721 transfer", e_3);
              setTxs(prev_6 => prev_6.map(te_2 => te_2.id === tid ? {
                ...te_2,
                state: "error",
                error: String(e_3?.shortMessage || e_3?.message || e_3)
              } : te_2));
            }
          } catch (error: any) {
            setTxs(prev_4 => prev_4.map(te_0 => te_0.id === tid ? {
              ...te_0,
              state: "error",
              error: String(error?.shortMessage || error?.message || error)
            } : te_0));
          }
        }
      }
    }
  }, [publicClient, address, t.selected, selectedWallet, walletClient, groupByContractAndOriginator]);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e_4: KeyboardEvent) => {
      if ((e_4.key === "Escape" || e_4.key === "Esc") && !trxPending) {
        e_4.preventDefault();
        handleClose();
      }
    };
    globalThis.addEventListener("keydown", onKeyDown);
    return () => globalThis.removeEventListener("keydown", onKeyDown);
  }, [open, trxPending, handleClose]);
  if (!open) return null;

  // Derive search helper text using helper
  const searchStatusText = getSearchStatusText(isSearching, needsMoreSearchCharacters, remainingSearchCharacters, results.length, trimmedQuery);
  const portalTarget = typeof document === "undefined" ? null : document.body;
  const modalContent = <dialog ref={dialogRef} open aria-modal="true" aria-labelledby="transfer-title" aria-describedby="transfer-desc" onCancel={e_5 => {
    e_5.preventDefault();
    if (!trxPending) handleClose();
  }} className={["tw-w-full tw-h-full tw-fixed tw-inset-0 tw-z-[1000] tw-bg-white/10 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center tw-p-0", isClosing ? "tw-opacity-0 tw-transition-opacity tw-duration-150" : "tw-opacity-100 tw-transition-opacity tw-duration-150"].join(" ")}>
      <div className={["tw-w-[95vw] tw-h-[90dvh] sm:tw-w-[90vw] sm:tw-h-[85dvh] md:tw-w-[70vw] md:tw-h-[75vh] tw-max-w-[1100px] tw-max-h-[900px] tw-rounded-2xl tw-bg-[#0c0c0d] tw-ring-[3px] tw-ring-white/30 tw-text-white tw-shadow-xl tw-overflow-hidden tw-flex tw-flex-col", isClosing ? "tw-scale-95 tw-opacity-0 tw-transition-all tw-duration-150" : "tw-scale-100 tw-opacity-100 tw-transition-all tw-duration-150", flow === "submission" || isClosing ? "tw-h-fit sm:tw-max-h-[90dvh] md:tw-max-h-[90vh]" : ""].join(" ")}>
        {/* header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-border-0 tw-border-b-[3px] tw-border-solid tw-border-white/30 tw-p-3 sm:tw-p-4">
          <div className="tw-text-base sm:tw-text-lg tw-font-semibold tw-flex-1 tw-min-w-0 tw-pr-2">
            <FlowTitle flow={flow} txs={txs} />
          </div>
          <HeaderRight flow={flow} trxPending={trxPending} onClose={handleClose} />
        </div>
        <p id="transfer-desc" className="tw-sr-only">
          {flow === "review" ? "Review selected NFTs and choose the destination recipient and wallet." : "Follow wallet prompts. Each transaction will indicate its current status."}
        </p>

        {/* body */}
        <BodyByFlow flow={flow} items={items_0} leftListRef={leftListRef} leftHasOverflow={leftHasOverflow} leftAtEnd={leftAtEnd} selectedProfile={selectedProfile} profile={profile} isIdentityLoading={isIdentityLoading} setSelectedProfile={setSelectedProfile} setSelectedWallet={setSelectedWallet} setResults={setResults} setQuery={setQuery} searchInputRef={searchInputRef} walletsListRef={walletsListRef} walletsHasOverflow={walletsHasOverflow} walletsAtEnd={walletsAtEnd} selectedWallet={selectedWallet} query={query} setQueryExternal={setQuery} searchStatusText={searchStatusText} results={results} onPick={r => {
        setSelectedProfile(r);
        setSelectedWallet(null);
      }} resultsListRef={resultsListRef} resultsHasOverflow={resultsHasOverflow} resultsAtEnd={resultsAtEnd} publicClient={publicClient} txs={txs} />

        {/* footer */}
        <div className="tw-flex tw-justify-between tw-items-center tw-gap-2 sm:tw-gap-3 tw-border-0 tw-border-t-[3px] tw-border-solid tw-border-white/30 tw-p-3 sm:tw-p-4">
          <div className="tw-flex tw-items-center tw-gap-2">
            {flow === "submission" && anyTxsPending(txs) && <>
                <FontAwesomeIcon icon={faExclamationTriangle} className="tw-size-8" color="#FFD60A" />
                <span className="tw-text-sm tw-font-medium tw-text-[#FFD60A]">
                  Double-check the recipient address and token details before
                  signing.
                  <br />
                  NFT transfers are irreversible once submitted on-chain.
                </span>
              </>}
          </div>
          <FooterActions flow={flow} canConfirm={!!canConfirm} onCancel={handleClose} onConfirm={handleConfirm} onClose={handleClose} txs={txs} />
        </div>
      </div>
    </dialog>;
  if (!isMounted || !portalTarget) return null;
  return createPortal(modalContent, portalTarget);
}